<template>
  <canvas ref="canvasRef" class="risk-map"></canvas>

  <div ref="tooltipRef" class="risk-area-tooltip">
    <span>{{ areaName }}</span>
    <span>(</span>
    <span>{{ riskScore }}</span>
    <span>)</span>
  </div>

  <el-dialog v-model="showAreaRiskDialog" title="区域风险" :width="750" align-center append-to-body>
    <AreaRiskView :map-id="props.mapId" :tag-id="tagId" />
  </el-dialog>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { fabric } from 'fabric'
import { fitToViewport } from '@/util/fab'
import AreaRiskView from '../components/AreaRiskView.vue'

defineExpose({ resize })
const props = defineProps({ mapId: Number, opacity: Number, data: Object, riskLevels: Array })
const loading = defineModel('loading')
const fab = ref(null)
const canvasRef = ref(null)
const colorMap = computed(() => {
  const opacity = props.opacity / 100.0 || 0.5
  return {
    0: `rgba(0, 0, 255, ${opacity})`,
    1: `rgba(255, 255, 0, ${opacity})`,
    2: `rgba(255, 165, 0, ${opacity})`,
    3: `rgba(255, 0, 0, ${opacity})`
  }
})
const levelMap = computed(() => {
  if (!props.riskLevels) return {}
  return props.riskLevels.reduce((map, cur) => {
    map[cur.tagId] = cur
    return map
  }, {})
})
const areaName = ref('')
const riskScore = ref(null)
const tooltipRef = ref(null)

const showAreaRiskDialog = ref(false)
const tagId = ref(null)

const current = ref({
  obj: null,
  target: null
})

watch([fab, levelMap, colorMap], () => {
  if (!fab.value) return
  fab.value.forEachObject((obj) => {
    updateRiskLevel(obj, levelMap.value)
  })
  fab.value.requestRenderAll()
})

watch([() => props.data, fab], renderMap)

onMounted(async () => {
  await nextTick() // 确保父组件DOM已渲染
  initMap()
  renderMap()
})

onUnmounted(() => {
  fab.value?.dispose()
})

/**
 * 初始化fabric实例,fabric实例在组件生命周期中只创建一次和销毁一次,但可能会多次更新
 */
function initMap() {
  const parentDOM = getParentDom()
  fab.value?.dispose()
  fab.value = new fabric.Canvas(canvasRef.value, {
    width: parentDOM.clientWidth,
    height: parentDOM.clientHeight,
    stopContextMenu: false,
    interactive: false,
    isDrawingMode: false,
    selection: false,
    preserveObjectStacking: true, //保持对象的层级关系
    hoverCursor: 'default',
    stopContextMenu: true, //禁止右键菜单
    contextOptions: {
      willReadFrequently: true //提高性能
    }
  })
  fab.value.on('mouse:out', onCanvasMouseOut) // 监听鼠标移出事件
}

/**
 * 当地图数据变化时重新渲染地图
 */
function renderMap() {
  const mapData = props.data
  if (!fab.value || !mapData) return
  //注销对象的事件处理器函数
  fab.value.forEachObject((obj) => obj.off())
  loading.value = true
  fab.value.loadFromJSON(
    mapData,
    () => {
      fitToViewport(fab.value) //适应视口
      loading.value = false
    },
    (o, obj) => {
      obj.set('selectable', false)

      if (!obj.my || !obj.my.risk) return

      updateRiskLevel(obj, levelMap.value)

      obj.set('perPixelTargetFind', true) // 启用像素级目标查找
      obj.set('hoverCursor', 'pointer')
      obj.on('mouseover', ({ target }) => {
        if (!obj.my || !obj.my.risk || !target) return
        target.set('opacity', 0.7)
        fab.value.requestRenderAll()
        tooltipRef.value.style.display = 'block'
        areaName.value = obj.my.name
        riskScore.value = obj.my.score || 0
        current.value.obj = obj
        current.value.target = target
      })
      obj.on('mousemove', ({ target, e }) => {
        if (!obj.my || !obj.my.risk || !tooltipRef.value) return

        const cur = tooltipRef.value
        const curWidth = cur.offsetWidth
        const curHeight = cur.offsetHeight

        const pa = cur.parentElement
        const paWidth = pa.clientWidth
        const paHeight = pa.clientHeight

        const canvasEl = canvasRef.value.parentElement //fabric容器元素
        let left = canvasEl.offsetLeft + e.offsetX
        let top = canvasEl.offsetTop + e.offsetY

        left = Math.min(left, paWidth - curWidth)
        top = Math.min(top, paHeight - curHeight)

        //设置tooltip的位置
        tooltipRef.value.style.left = `${left}px`
        tooltipRef.value.style.top = `${top}px`

        current.value.obj = obj
        current.value.target = target
      })
      obj.on('mouseout', ({ target }) => {
        if (!obj.my || !obj.my.risk || !target) return
        target.set('opacity', 1)
        fab.value.requestRenderAll()
        tooltipRef.value.style.display = 'none'
      })
      obj.on('mousedown', () => {
        if (!obj.my || !obj.my.risk) return
        tagId.value = obj.my.id
        showAreaRiskDialog.value = true
        tooltipRef.value.style.display = 'none'
      })
    }
  )
}

/**
 * 更新四色图区域风险等级
 */
function updateRiskLevel(obj, levelMap) {
  if (!obj.my || !obj.my.risk) return
  const pr = levelMap[obj.my.id]
  if (pr !== null && pr !== undefined) {
    obj.my.level = pr.level
    obj.my.score = pr.score
    obj.set('fill', colorMap.value[pr.level])
  } else {
    obj.set('fill', colorMap.value[0])
  }
}

function resize() {
  const parentDOM = getParentDom()
  if (!parentDOM || !fab.value) return
  fab.value.setDimensions({ width: parentDOM.clientWidth, height: parentDOM.clientHeight })
  fitToViewport(fab.value) //适应视口
}

function getParentDom() {
  let target = canvasRef.value.parentElement
  while (true) {
    if (target == null) return null
    if (target.classList.contains('el-carousel__item')) return target
    target = target.parentElement
  }
}

function onCanvasMouseOut() {
  const { obj, target } = current.value
  if (!obj || !target) return
  target.set('opacity', 1)
  fab.value.requestRenderAll()
  tooltipRef.value.style.display = 'none'
}
</script>

<style lang="scss">
$bg-color: rgba(0, 24, 72, 0.7);

.risk-area-tooltip {
  color: white;
  background-color: rgba(0, 24, 72, 0.5);
  position: absolute;
  z-index: 1000;
  padding: 5px 15px;
  border-radius: 4px;
  pointer-events: none;
  white-space: nowrap;
  display: none; /* 初始隐藏 */
}
</style>
